import { useMemo, useRef, useState } from "react";
import { NavLink, Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useStudio } from "./StudioContext";
import { cx, resolveDept } from "./StudioShared";
import {
  LayoutDashboard, BrainCircuit, Target, Users, BookOpen, Building2, GitMerge,
  BarChart3, Server, Wrench, Workflow, Database, Network, CheckSquare, HardDrive,
  Dumbbell, Zap, Settings, Lock, Search, LogOut, Home, ChevronRight, Radio, TrendingUp, Activity, Cpu, FileDown, GraduationCap, ShieldCheck, Split, Mail,
} from "lucide-react";

const B = "/admin/studio";
const NAV_GROUPS = [
  { label: "Overview", items: [
    { to: `${B}/command-center`, label: "Command Center", icon: Radio, id: "command-center" },
    { to: `${B}/dashboard`, label: "Studio Dashboard", icon: LayoutDashboard, id: "dashboard" },
    { to: `${B}/executive-intelligence`, label: "Executive Intelligence", icon: TrendingUp, id: "executive-intelligence" },
    { to: `${B}/mission-intelligence`, label: "Mission Intelligence", icon: BrainCircuit, id: "mission-intelligence" },
    { to: `${B}/ai-intelligence`, label: "AI Intelligence Center", icon: Activity, id: "ai-intelligence" },
    { to: `${B}/intelligence-router`, label: "Intelligence Router", icon: Split, id: "intelligence-router" },
  ]},
  { label: "Growth", items: [
    { to: `${B}/subscribers`, label: "Newsletter Subscribers", icon: Mail, id: "subscribers" },
  ]},
  { label: "Execution", items: [
    { to: `${B}/missions`, label: "Mission Explorer", icon: Target, id: "missions" },
  ]},
  { label: "Discovery", items: [
    { to: `${B}/strategists`, label: "Strategist Explorer", icon: Users, id: "strategists" },
    { to: `${B}/knowledge`, label: "Knowledge Explorer", icon: BookOpen, id: "knowledge" },
    { to: `${B}/departments`, label: "Department Explorer", icon: Building2, id: "departments" },
    { to: `${B}/workflows`, label: "Workflow Explorer", icon: GitMerge, id: "workflows" },
  ]},
  { label: "Creation", items: [
    { to: `${B}/strategist-builder`, label: "Strategist Builder", icon: Wrench, id: "strategist-builder" },
    { to: `${B}/mission-manager`, label: "Mission Manager", icon: Target, id: "mission-manager" },
    { to: `${B}/workflow-builder`, label: "Workflow Builder", icon: Workflow, id: "workflow-builder" },
    { to: `${B}/knowledge-manager`, label: "Knowledge Manager", icon: Database, id: "knowledge-manager" },
    { to: `${B}/training-center`, label: "Training Center", icon: GraduationCap, id: "training-center" },
  ]},
  { label: "Organization", items: [
    { to: `${B}/department-manager`, label: "Department Manager", icon: Network, id: "department-manager" },
    { to: `${B}/memory-center`, label: "Memory Center", icon: BrainCircuit, id: "memory-center" },
  ]},
  { label: "System", items: [
    { to: `${B}/evaluation-center`, label: "Evaluation Center", icon: CheckSquare, id: "evaluation-center" },
    { to: `${B}/automation-center`, label: "Automation Center", icon: Zap, id: "automation-center" },
    { to: `${B}/audit-center`, label: "Audit Center", icon: ShieldCheck, id: "audit-center" },
    { to: `${B}/resource-manager`, label: "AI Resource Manager", icon: Cpu, id: "resource-manager" },
    { to: `${B}/export-center`, label: "Export Center", icon: FileDown, id: "export-center" },
    { to: `${B}/enterprise-settings`, label: "Enterprise Settings", icon: Settings, id: "enterprise-settings" },
    { to: `${B}/cost-dashboard`, label: "Cost & Transparency", icon: BarChart3, id: "cost-dashboard" },
    { to: `${B}/roi`, label: "ROI Dashboard", icon: TrendingUp, id: "roi" },
    { to: `${B}/analytics`, label: "Analytics", icon: Activity, id: "analytics" },
    { to: `${B}/providers`, label: "Provider Dashboard", icon: Server, id: "providers" },
  ]},
];
const RESERVED_GROUPS = [];

const ACTIVE_TITLES = NAV_GROUPS.flatMap((g) => g.items).reduce((m, i) => ((m[i.id] = i.label), m), {});

export default function StudioLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const seg = location.pathname.split("/")[3] || "dashboard";
  const crumb = ACTIVE_TITLES[seg] || "Studio";

  return (
    <div className="min-h-screen bg-[#070E20] text-white flex font-body">
      {/* Sidebar */}
      <aside className="hidden lg:flex fixed left-0 h-screen w-64 bg-[#0A1128] border-r border-white/10 flex-col z-40" data-testid="studio-sidebar">
        <Link to={`${B}/dashboard`} className="flex items-center gap-3 px-6 h-16 border-b border-white/10 shrink-0">
          <div className="w-9 h-9 border border-gold flex items-center justify-center font-heading font-bold text-gold rounded-sm">AI</div>
          <div>
            <div className="font-heading text-white font-semibold leading-none tracking-wide">AIPOS Studio</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-gold mt-0.5">Mission Control</div>
          </div>
        </Link>
        <nav className="flex-1 py-4 px-3 space-y-4 overflow-y-auto">
          {NAV_GROUPS.map((g) => (
            <div key={g.label}>
              <div className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{g.label}</div>
              <div className="space-y-0.5">
                {g.items.map((it) => (
                  <NavLink key={it.id} to={it.to} data-testid={`nav-link-${it.id}`}
                    className={({ isActive }) => cx("w-full flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors",
                      isActive ? "bg-gold/15 text-gold border-l-2 border-gold" : "text-slate-400 hover:text-white hover:bg-white/5")}>
                    <it.icon size={16} /> {it.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
          <div className="pt-2 border-t border-white/5" />
          {RESERVED_GROUPS.map((g) => (
            <div key={g.label}>
              <div className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-600">{g.label}</div>
              <div className="space-y-0.5">
                {g.items.map((it) => (
                  <div key={it.id} data-testid={`nav-link-${it.id}`} title="Coming in Phase 2"
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-sm text-slate-600 opacity-60 cursor-not-allowed select-none">
                    <it.icon size={16} /> <span className="flex-1">{it.label}</span> <Lock size={12} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1 shrink-0">
          <Link to="/admin" className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-400 hover:text-white rounded-sm" data-testid="studio-back-admin"><Home size={15} /> Back to Admin</Link>
          <button onClick={logout} data-testid="studio-logout" className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-400 hover:text-white rounded-sm"><LogOut size={15} /> Sign out</button>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 lg:ml-64 min-w-0 flex flex-col">
        <TopBar crumb={crumb} user={user} />
        <MobileNav />
        <main className="p-5 md:p-8 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function TopBar({ crumb, user }) {
  const { health, providers } = useStudio();
  const active = providers?.active_provider;
  const healthy = !!active && health?.enabled;
  return (
    <header className="h-16 border-b border-white/10 bg-[#0A1128] flex items-center gap-4 px-5 md:px-8 sticky top-0 z-30" data-testid="studio-topbar">
      <div className="flex items-center gap-2 text-sm text-slate-400 min-w-0">
        <span className="text-slate-500 hidden sm:inline">AIPOS Studio</span>
        <ChevronRight size={14} className="text-slate-600 hidden sm:inline" />
        <span className="text-white font-medium truncate" data-testid="studio-breadcrumb">{crumb}</span>
      </div>
      <div className="flex-1 max-w-md hidden md:block"><GlobalSearch /></div>
      <div className="ml-auto flex items-center gap-4 shrink-0">
        <div className="flex items-center gap-2 px-3 py-1.5 border border-white/10 rounded-sm" data-testid="studio-provider-pill">
          <Radio size={13} className={healthy ? "text-emerald-400" : "text-amber-400"} />
          <span className="text-xs text-slate-300 capitalize">{active || "no provider"}</span>
          <span className={cx("w-1.5 h-1.5 rounded-full", healthy ? "bg-emerald-400" : "bg-amber-400")} />
        </div>
        <div className="w-8 h-8 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center text-gold text-sm font-semibold" title={user?.email} data-testid="studio-user">
          {(user?.name || user?.email || "?")[0]?.toUpperCase()}
        </div>
      </div>
    </header>
  );
}

function GlobalSearch() {
  const { strategists, knowledge, missions, providers } = useStudio();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef();

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    const out = [];
    (strategists || []).forEach((s) => {
      if ((s.name + s.id + (s.domains || []).join(" ") + resolveDept(s).name).toLowerCase().includes(term))
        out.push({ type: "Strategist", label: s.name, sub: s.code || s.id, route: `/admin/studio/strategists?focus=${s.id}` });
    });
    (knowledge?.entries || []).forEach((k) => {
      if ((k.title + k.domain + (k.tags || []).join(" ")).toLowerCase().includes(term))
        out.push({ type: "Knowledge", label: k.title, sub: k.domain, route: `/admin/studio/knowledge?focus=${k.id}` });
    });
    (missions || []).forEach((m) => {
      if (((m.org_name || "") + (m.document?.headline || "") + (m.context?.data?.industry || "")).toLowerCase().includes(term))
        out.push({ type: "Mission", label: m.org_name || "Untitled", sub: `v${m.version}`, route: `/admin/studio/missions?focus=${m.id}` });
    });
    (providers?.providers || []).forEach((p) => {
      if (p.name.toLowerCase().includes(term)) out.push({ type: "Provider", label: p.name, sub: p.available ? "active" : "dormant", route: `/admin/studio/providers` });
    });
    return out.slice(0, 12);
  }, [q, strategists, knowledge, missions, providers]);

  const go = (r) => { setOpen(false); setQ(""); navigate(r.route); };

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center gap-2 bg-navy-800 border border-white/10 rounded-sm px-3 h-9">
        <Search size={15} className="text-slate-500" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Search strategists, knowledge, missions…" data-testid="studio-global-search"
          className="bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none flex-1" />
      </div>
      {open && q.trim() && (
        <div className="absolute top-11 left-0 right-0 bg-[#0A1128] border border-white/10 rounded-sm shadow-2xl max-h-80 overflow-y-auto z-50" data-testid="studio-search-results">
          {results.length === 0 ? <div className="p-4 text-sm text-slate-500">No matches.</div> :
            results.map((r, i) => (
              <button key={i} onMouseDown={() => go(r)} className="w-full text-left px-4 py-2.5 hover:bg-white/5 flex items-center gap-3 border-b border-white/5 last:border-0" data-testid={`search-result-${i}`}>
                <span className="text-[9px] uppercase tracking-wider text-gold border border-gold/40 px-1.5 py-0.5 rounded-sm shrink-0 w-20 text-center">{r.type}</span>
                <span className="text-sm text-white truncate flex-1">{r.label}</span>
                <span className="text-xs text-slate-500 shrink-0">{r.sub}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

function MobileNav() {
  const allItems = NAV_GROUPS.flatMap((g) => g.items);
  return (
    <div className="lg:hidden flex gap-1 overflow-x-auto p-3 border-b border-white/10 bg-[#0A1128] sticky top-16 z-20">
      {allItems.map((it) => (
        <NavLink key={it.id} to={it.to} data-testid={`mnav-link-${it.id}`}
          className={({ isActive }) => cx("flex items-center gap-1.5 px-3 py-2 text-xs whitespace-nowrap rounded-sm", isActive ? "bg-gold text-navy" : "text-slate-400")}>
          <it.icon size={14} /> {it.label.replace(" Explorer", "").replace("Studio ", "")}
        </NavLink>
      ))}
    </div>
  );
}
